import { Megaphone, Users, Trophy, TrendingUp, PlusCircle, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { PageHeader } from '@/components/dashboard/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { Button } from '@/components/ui/button';
import { campaigns, participationsTrend, fmt, gameLabels } from '@/lib/mockData';

const totals = campaigns.reduce(
  (acc, c) => ({
    participations: acc.participations + c.participations,
    prizes: acc.prizes + c.prizesGiven,
    active: acc.active + (c.status === 'active' ? 1 : 0),
  }),
  { participations: 0, prizes: 0, active: 0 }
);

const PartenaireDashboard = () => (
  <DashboardLayout
    role="partenaire"
    user={{ name: 'Marie Koné', subtitle: 'Orange CI', initials: 'MK' }}
  >
    <PageHeader
      title="Vue d'ensemble"
      subtitle="Performance globale de tes activations Winkô."
      actions={
        <>
          <Button variant="outline" size="sm">
            <Download className="size-4" />
            Export
          </Button>
          <Button size="sm" asChild className="shadow-cta">
            <Link to="/partenaire/nouvelle">
              <PlusCircle className="size-4" />
              Nouvelle campagne
            </Link>
          </Button>
        </>
      }
    />

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Campagnes actives"
        value={totals.active}
        delta={{ value: '2 ce mois-ci', positive: true }}
        icon={Megaphone}
        accent="primary"
      />
      <StatCard
        label="Participations totales"
        value={fmt.number(totals.participations)}
        delta={{ value: '+47 % vs mois dernier', positive: true }}
        icon={Users}
        accent="accent"
      />
      <StatCard
        label="Lots distribués"
        value={fmt.number(totals.prizes)}
        delta={{ value: '+312 cette semaine', positive: true }}
        icon={Trophy}
        accent="emerald"
      />
      <StatCard
        label="Taux d'engagement"
        value="68 %"
        delta={{ value: '+8 pts', positive: true }}
        icon={TrendingUp}
        accent="amber"
      />
    </div>

    <div className="mt-8 grid gap-6 lg:grid-cols-3">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-soft lg:col-span-2">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold">Participations & gains — 14 jours</h3>
            <p className="text-xs text-muted-foreground">
              Évolution sur l'ensemble de tes campagnes actives.
            </p>
          </div>
        </div>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={participationsTrend} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="day"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 12,
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="participations"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#gP)"
              />
              <Area
                type="monotone"
                dataKey="gains"
                stroke="hsl(var(--accent))"
                strokeWidth={2}
                fill="url(#gG)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h3 className="font-display text-lg font-bold">Mix des jeux</h3>
        <p className="text-xs text-muted-foreground">Répartition des participations par type.</p>
        <ul className="mt-5 space-y-4">
          {[
            { game: 'grattage' as const, value: 62 },
            { game: 'roulette' as const, value: 24 },
            { game: 'tombola' as const, value: 14 },
          ].map(row => (
            <li key={row.game}>
              <div className="mb-1.5 flex items-center justify-between text-sm">
                <span className="font-medium">{gameLabels[row.game]}</span>
                <span className="text-muted-foreground">{row.value} %</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-primary"
                  style={{ width: `${row.value}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="mt-8 rounded-2xl border border-border bg-card shadow-soft">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <h3 className="font-display text-lg font-bold">Mes campagnes</h3>
          <p className="text-xs text-muted-foreground">Top 5 par volume de participations.</p>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/partenaire/campagnes">Voir tout</Link>
        </Button>
      </div>
      <ul className="divide-y divide-border">
        {campaigns.slice(0, 5).map(c => (
          <li
            key={c.id}
            className="grid grid-cols-1 items-center gap-3 px-5 py-4 sm:grid-cols-[1.5fr_1fr_1fr_auto]"
          >
            <div>
              <p className="font-semibold">{c.name}</p>
              <p className="text-xs text-muted-foreground">
                {c.brand} • {gameLabels[c.game]}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Participations</p>
              <p className="font-display text-base font-bold">{fmt.number(c.participations)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Lots remis</p>
              <p className="font-display text-base font-bold">
                {fmt.number(c.prizesGiven)}{' '}
                <span className="text-xs font-normal text-muted-foreground">/ {c.totalPrizes}</span>
              </p>
            </div>
            <StatusBadge status={c.status} />
          </li>
        ))}
      </ul>
    </div>
  </DashboardLayout>
);

export default PartenaireDashboard;
